import Button from 'components/shared/app/Button';
import Loader from 'components/shared/app/Loader';
import copy from 'copy-to-clipboard';
import { ErrorMessages, isInvalidTokenError, isRateLimitError } from 'lib/AppErrors';
import { Goal } from 'lib/data/models/Goals';
import NotificationService from 'lib/services/NotificationService';
import { ProviderNames } from 'models/Providers';
import moment from 'moment-timezone';
import React from 'react';
import AuthService from 'services/AuthService';
import { orZero } from '../NumUtils';
import { tryJsonParse } from '../ObjectUtils';
import { ellipsesText } from '../StrUtils';
import { isLocalhost } from '../UiUtils';
import { ignoreProviderForDev } from './ProviderUtils';

export const CURRENT_PROVIDERS = [
    ProviderNames.SHOPIFY,
    ProviderNames.ADAYA,
    ProviderNames.FACEBOOK,
    ProviderNames.GOOGLE,
    ProviderNames.TIKTOK
];

/**
 * @description Returns the given (or current) user's activeAccount, or throws an Exception if one is not found.
 * @param {*} [user]
 * @return {*}
 */
export const getAccountOrThrow = (user?) => {
    user = user || AuthService.getCurrentUser();
    if (user?.activeAccount) return user.activeAccount;
    throw new Exception("User does not exist or does not have an active account.");
}

/**
 * @description Tries to find account's timezone, or defaults to user's local.
 * @param {*} account
 * @return {*}
 */
export const getAccountTimezone = (account) => {
    if (account?.storeInfo?.timezone) {
        return account.storeInfo.timezone;
    }
    let tz = moment.tz.guess();
    console.warn('Account has no store timezone set, using default local timezone:', tz, ' Account:', account);
    return tz;
}

/**
 * @description Shortcut to retrieve the active account's provider data, if it's valid (has oauth data).
 * @param {*} account
 * @param {*} providerName
 * @return {*}
 */
export const getAccountProvider = (account, providerName) => {
    if (account && account.providers && providerName) {
        const provider = account.providers.find((p) => p.name === providerName);
        if (provider && provider.oauth) {
            return provider;
        }
    }
    return undefined;
}

/**
 * @description Returns the actual provider instances/data for the account, but only the ones we currently can actually handle.
 * ie. this is just a convenience method to filter in one place, and also ensure the Adaya provider.
 * @param {Account} account
 */
// Todo: reverse the omit and only params.
export const getAccountProviders = (account: Account, omitProviders?: Array<string>, onlyProviders?: Array<string>): Array<any> => {
    if (!account) {
        console.warn("Warning: account was not given to getAccountProviders");
        return [];
    }

    // get all the supported providers on this account:
    let providers: any = (account.providers || []).filter((p: any) => {
        if (omitProviders?.length && omitProviders.includes(p.name)) return false;
        if (!p.oauth) return false; // exclude providers without oauth/not initialized
        return CURRENT_PROVIDERS.includes(p.name);
    });

    // we manually add the Adaya provider for now (it is not registered/setup anywhere officially)
    if (!providers.find((p: any) => p.name == ProviderNames.ADAYA)) {
        // only add adaya if account has tracking initialized
        if (account.tracking) {
            // always add Adaya provider unless we want to omit it for some reason:
            if (omitProviders) {
                if (!omitProviders.includes(ProviderNames.ADAYA)) {
                    providers.push({ name: ProviderNames.ADAYA, oauth: false });
                }
            } else {
                providers.push({ name: ProviderNames.ADAYA, oauth: false });
            }
        }
    }

    // todo: finish state integration
    //let providerStates = ProviderHelper.getProviderStates();
    providers = providers.filter(p => !ignoreProviderForDev(p.name));

    if (onlyProviders?.length) {
        providers = providers.filter(p => onlyProviders.includes(p.name));
    }
    return providers;
}

// convenience to get string representation of provider data provider names, from single or array.
export const makeProviderNames = (pd) => {
    return `${Array.isArray(pd) ? pd.map(p => p.name).join(', ') : pd.name}`;
}

export const getProviderNameFromUtmSource = (utmSource): ProviderNames | undefined => {
    if (!utmSource) return undefined;
    switch (utmSource.toLowerCase()) {
        case "ig":
        case "instagram":
        case "fb":
        case "facebook":
        case "meta":
            return ProviderNames.FACEBOOK;
        case "google":
        case "cpc":
            return ProviderNames.GOOGLE;
        case "klaviyo":
            return ProviderNames.KLAVIYO;
        case "shopify":
            return ProviderNames.SHOPIFY;
        case "amazon":
            return ProviderNames.AMAZON;
        case "tiktok":
            return ProviderNames.TIKTOK;
        default:
            return undefined;
    }
}

// hacky way to maintain global state of notifications (namely just errors) for now.
let hideTokenErrors = false;
/**
 * @description Takes an arbitrary server error and tries to show a notification from it, depending on some filtering.
 * @param {*}
 */
// TODO: maybe move this...
export const handleResponseErrorNotifications = (e, onHideErrors?) => {
    if (!e) return;

    //console.log(`handleResponseErrorNotifications`, e)
    errorNotifications = [];

    // This should really be handled somewhere else closer to the UI, maybe... or let the NotificationService handle it maybe.
    const _onHideErrors = () => {
        hideTokenErrors = true;
        NotificationService.closeAllNotifications();
        NotificationService.notifySuccess('Provider API Errors Hidden', 'Refreshing the page will show them again.', { duration: 3 });
        errorNotifications = [];
        if (onHideErrors) onHideErrors(e)
    }

    const _getMsgHtml = (msg, showHide = false) => {
        return <div>
            <div dangerouslySetInnerHTML={{ __html: msg.replace('hello@adaya.ai', '<a href="mailto:hello@adaya.ai">hello@adaya.ai</a>') }}></div>
            {showHide && <Button key="hide-errors" style="margin-top: 10px;" className="btn-hide-errors" round filled color="blue" size="small" onClick={_onHideErrors}>Disable These Errors</Button>}
        </div>
    }

    let errors = undefined;

    const showError = (title, msg, isTokenError = false) => {
        if (isTokenError && hideTokenErrors) {
            console.log(`Not showing error because it's a token error:`, title, msg);
        } else {
            let _e = NotificationService.notifyError(title, msg, { duration: 0 });
            errorNotifications.push(_e);
        }
    }

    // break apart individual provider errors if necessary
    if (e.name == 'AdayaError') {
        errors = (e.data?.type == 'ProviderErrors') ? e.data.errors : [e];
    } else {
        errors = Array.isArray(e) ? e : [e];
    }

    if (errors) {
        errors.forEach(_e => {
            let msg = getInnerError(_e);
            let isInvalidToken = isInvalidTokenError(msg);
            let isRateLimited = isRateLimitError(msg);
            msg = isInvalidToken ? ErrorMessages.fixApiTokenError(_e.provider) :
                isRateLimited ? ErrorMessages.apiRateLimitError(_e.provider) :
                    msg;
            showError(`${_e.provider ? (_e.provider + ' ') : ''}API Error`, _getMsgHtml(msg, isInvalidToken), isInvalidToken);
        });
    }

    return errors;
}

// Finds the inner most error
export const getInnerError = (e) => {
    if (!e) return undefined;
    if (typeof e == 'string') return e;
    if (Array.isArray(e)) return e.map(_e => getInnerError(_e)).join(', ');
    // leave these separate so it recursesq through to each to always ensure a message can be found.
    return getInnerError(e.error) ||
        getInnerError(e.errors) ||
        getInnerError(e.data) ||
        getInnerError(e.response) ||
        getInnerError(e.message) ||
        getInnerError(e.reason) ||
        'Unknown error.';
}

// Utility to return a wrapped error message in some nice text, at least.
export const renderError = (label, errorMessage, size = '') => (
    <div className={`error full ${size}`}>
        {label && <div className="error-label">{label}</div>}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
    </div>
);

export const renderLoader = (showText = false) => {
    const loaderProps = {
        showText: (typeof showText == 'string') ? true : showText,
        customText: (typeof showText == 'string') ? showText : undefined
    }
    return <div style={{ margin: "auto" }}><Loader {...loaderProps} /></div>
};

export const renderNoDataMessage = () => (
    <div className="no-data">{ErrorMessages.noDataError()}</div>
);


// pulls the given goal from the Account, or returns a default.
export const getGoal = (account: Account, goal: Goal) => {
    try {
        // calculated rules:
        // const adspendGoal = (revenueGoal / merGoal);
        // const cppGoal = (adspend goal / orders goal)
        // const purchasesGoal = (aovGoal == 0) ? 0 : orZero((revenueGoal / aovGoal).toFixed(2));
        // roas goal = tracked revenue / adspend;
        // mer goal = shopify revenue / adspend;
        //console.log(`getGoal`, goal, account.metadata.goals)

        if (!account || !goal) return 0;
        let _goal = goal.toLowerCase();
        let altKey = `${_goal}Goal`; // for old goal format.
        let goalVal = 0;

        // just return basic goals without calculation:
        if ([Goal.Roas, Goal.Aov, Goal.Mer, Goal.Revenue].includes(goal)) {
            // load basic goals from metadata:
            if (typeof account?.metadata?.goals[_goal] != 'undefined') {
                //console.log(`getGoal ${_goal}`,account?.metadata?.goals[_goal])
                goalVal = orZero(account?.metadata?.goals[_goal]);
            } else if (typeof account?.metadata?.goals[altKey] != 'undefined') {
                //console.log(`getGoal ${altKey}`,account?.metadata?.goals[altKey])
                goalVal = orZero(account?.metadata?.goals[altKey]);
            }

            //console.log(`basic goal: ${goal}`, goalVal)
            return parseFloat(goalVal.toFixed(2));
        }

        const revenueGoal = getGoal(account, Goal.Revenue);
        const merGoal = getGoal(account, Goal.Mer);
        const roasGoal = getGoal(account, Goal.Roas);
        const aovGoal = getGoal(account, Goal.Aov);

        if (goal == Goal.Cpp) {
            // == adspendGoal / ordersGoal
            goalVal = orZero(((revenueGoal) / (merGoal)) / ((revenueGoal) / (aovGoal)));
        }

        if (goal == Goal.Orders) {
            goalVal = orZero((revenueGoal / aovGoal));
        }

        if (goal == Goal.Adspend) {
            //goalVal = orZero((revenueGoal / roasGoal));
            goalVal = orZero((revenueGoal / merGoal));
        }

        //console.log(`calculated goal: ${goal}`, goalVal)
        return goalVal ? parseFloat(goalVal.toFixed(2)) : 0;
    } catch (e) {
        console.log(`Error getting goal: ${goal}, for account: ${accountName(account)}`, getInnerError(e));
        return 0;
    }
}

// only render the given content if the user is a master/site Admin.
// if a user is passed, it will check against that.
export const renderAdminContent = (content: any, user?, forceRender = false) => {
    user = user || AuthService.getCurrentUser();
    return (isSiteAdmin(user) || (isLocalhost() && showAdminOnLocalhost()) || forceRender) ? content : undefined;
}

export const showAdminOnLocalhost = () => {
    return true; // TODO: Load from env.
}

// Copies text to clipboard and shows Notification
export const copyToClipboard = (text, showNotification = true) => {
    copy(text);
    let subStr = ellipsesText(text, 50);
    NotificationService.notifySuccess('Success', `'${subStr}' copied to clipboard!`);
}

export const isSiteAdmin = (user) => {
    return (user && user.roles && user.roles.indexOf('admin') > -1) ? true : false;
}

// Returns true if the user is not in activeAccount's subUsers (ie. is owner), or otherwise is with role == 'admin'.
export const isAccountAdmin = (user) => {
    const account = user?.activeAccount;
    if (!account?.accountUsers) return true; // assume no users means this is the admin
    const accountUser = account.accountUsers.find(au => au.email === user.email);
    return (!accountUser || accountUser.role == 'admin');
}

// Returns true is user is not in activeAccount's subUsers.
export const isAccountOwner = (user) => {
    const account = user?.activeAccount;
    if (!account.accountUsers) return false;
    const accountUsers = user.activeAccount.accountUsers.filter(au => au.email === user.email);
    return accountUsers.length > 0 ? false : true;
}

export const accountName = (account) => {
    return account ? ` [${account.name} (${account._id})]` : '(NULL)';
}

// returns either the order_id, if there is one, or the event_id,
// to produce a unique integer to represent the event for any purpose.
export const getEventID = (event) => {
    if (!event) return '';
    if (event.order_id) return `${event.order_id}`;
    if (event.event_id) return `${event.event_id}`;
    let ed = tryJsonParse(event.data, {});
    if (ed.eventID) return `${ed.eventID}`;
}
